import type { UseFormReturn } from 'react-hook-form';
import { ColorPaletteGroupProvider, Labeled } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IImageCreatorFormValues } from './ImageCreatorForm';
import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';
import { CustomColorInputField } from '~/components/organisms/fields/CustomColorInputField';
import { FontAwesomeIconSelector } from '~/components/organisms/fields/FontAwesomeIconSelector';
import {
  IFontAwesomeIconFamily,
  IFontAwesomeIconStyle,
} from '~/hooks/useFontAwesomeSearchIconsQuery';

export type IImageCreatorFormMainSectionProps = IContainerProps & {
  form: UseFormReturn<IImageCreatorFormValues>;
  appId: string;
};

export const ImageCreatorFormMainSection: React.FC<
  IImageCreatorFormMainSectionProps
> = (props) => {
  const { sx, form, appId } = props;
  const { t } = useTranslation();

  return (
    <FormSection sx={sx}>
      <ColorPaletteGroupProvider>
        <Labeled
          label={t('ImageCreatorFormMainSection.field.backgroundColor.label')}
          required
        >
          <FormField
            name="backgroundColor"
            control={form.control}
            as={CustomColorInputField}
            forwardError
            palette="app"
            appId={appId}
          />
        </Labeled>
        <Labeled
          label={t(
            'ImageCreatorFormMainSection.field.backgroundGradientColor.label',
          )}
        >
          <FormField
            name="backgroundGradientColor"
            control={form.control}
            as={CustomColorInputField}
            forwardError
            palette="app"
            appId={appId}
            clearable
            placeholder={t(
              'ImageCreatorFormMainSection.field.backgroundGradientColor.placeholder',
            )}
          />
        </Labeled>
        <Labeled
          label={t('ImageCreatorFormMainSection.field.foregroundColor.label')}
        >
          <FormField
            name="foregroundColor"
            control={form.control}
            as={CustomColorInputField}
            forwardError
            palette="app"
            appId={appId}
            clearable
            placeholder={t(
              'ImageCreatorFormMainSection.field.foregroundColor.placeholder',
            )}
          />
        </Labeled>
        <Labeled
          label={t('ImageCreatorFormMainSection.field.icon.label')}
          supportingText={t(
            'ImageCreatorFormMainSection.field.icon.supportingText',
          )}
        >
          <FormField
            name="icon"
            control={form.control}
            as={FontAwesomeIconSelector}
            forwardError
            family={IFontAwesomeIconFamily.Classic}
            style={IFontAwesomeIconStyle.Solid}
            placeholder="Example: music"
          />
        </Labeled>
      </ColorPaletteGroupProvider>
    </FormSection>
  );
};
